(() => {
  const STORAGE_KEY = 'lamps:room-submissions:v1';
  const MAX_STORED_SUBMISSIONS = 50;

  const DEFAULT_I18N = {
    offer_link: 'Add your room to shopper photos',
    offer_title: 'Add your room to this product\'s gallery?',
    offer_body: 'Other shoppers see your preview when they view this light. Your name and email are never shown.',
    consent_label: 'I own this photo and no one is pictured in it.',
    room_name_label: 'Room name (optional)',
    room_name_placeholder: 'Dining room',
    submit: 'Submit for review',
    decline: 'No thanks',
    submitted: 'Submitted. We review within a few days. You can remove it any time from the link we keep in this browser.',
    withdraw: 'Remove my submission',
    withdrawn: 'Your submission was removed.',
    error_duplicate: 'This preview was already submitted.',
    error_rate_limited: 'You\'ve submitted several rooms. Please try again later.',
    error_generic: 'Couldn\'t submit right now. Try again.',
    strip_heading: 'In {count} real rooms',
    yours: 'Yours',
    caption_labelled: 'In a customer\'s {room} · AI preview',
    caption_plain: 'In a customer\'s room · AI preview',
    share_page_heading: 'See it in other rooms',
  };

  let cachedI18n = null;

  function resolveI18n() {
    if (cachedI18n) return cachedI18n;
    let overrides = null;
    const shareScript = document.querySelector('script[type="application/json"][data-real-rooms-i18n]');
    if (shareScript) {
      try { overrides = JSON.parse(shareScript.textContent); } catch { overrides = null; }
    }
    if (!overrides) {
      const strip = document.querySelector('real-rooms-strip[data-i18n]');
      if (strip) {
        try { overrides = JSON.parse(strip.dataset.i18n); } catch { overrides = null; }
      }
    }
    cachedI18n = { ...DEFAULT_I18N, ...(overrides || {}) };
    return cachedI18n;
  }

  function format(template, vars) {
    return String(template).replace(/\{(\w+)\}/g, (match, key) => (key in vars ? String(vars[key]) : match));
  }

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;',
    }[character]));
  }

  function isHttpsImageUrl(value) {
    if (typeof value !== 'string' || !value) return false;
    try {
      const url = new URL(value, location.origin);
      return url.protocol === 'https:' && !url.username && !url.password;
    } catch {
      return false;
    }
  }

  function track(name, jobId, product, extra = {}) {
    window.LampsRoomStudio?.track(name, { product, jobId }, extra);
  }

  function readSubmissions() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function writeSubmissions(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(-MAX_STORED_SUBMISSIONS)));
    } catch {
      /* private-mode or full storage: submission state just won't persist across reloads */
    }
  }

  function storeSubmission(jobId, takedown_token) {
    if (!jobId || !takedown_token) return;
    const list = readSubmissions().filter((entry) => entry.jobId !== jobId);
    list.push({ jobId, takedown_token });
    writeSubmissions(list);
  }

  function findSubmission(jobId) {
    return readSubmissions().find((entry) => entry.jobId === jobId) || null;
  }

  function removeSubmission(jobId) {
    writeSubmissions(readSubmissions().filter((entry) => entry.jobId !== jobId));
  }

  class RoomSubmitCard extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.fieldId = `RoomSubmitCard-${Math.random().toString(36).slice(2, 8)}`;
      this.error = '';
      const existing = this.jobId ? findSubmission(this.jobId) : null;
      this.state = existing ? 'submitted' : 'collapsed';
      this.render();
    }

    workerUrl() {
      const host = this.closest('[data-worker-url]');
      return (host?.dataset.workerUrl || '').replace(/\/$/, '');
    }

    render() {
      const i18n = resolveI18n();

      if (this.state === 'collapsed') {
        this.innerHTML = `<a href="#" class="room-submit-card__link" data-offer-link>${escapeHtml(i18n.offer_link)}</a>`;
        this.querySelector('[data-offer-link]').addEventListener('click', (event) => {
          event.preventDefault();
          this.state = 'expanded';
          this.render();
        });
        return;
      }

      if (this.state === 'submitted') {
        this.innerHTML = `
          <div class="room-submit-card room-submit-card--submitted">
            <p class="room-submit-card__message" role="status">${escapeHtml(i18n.submitted)}</p>
            <a href="#" class="room-submit-card__link" data-withdraw-link>${escapeHtml(i18n.withdraw)}</a>
          </div>`;
        this.querySelector('[data-withdraw-link]').addEventListener('click', (event) => {
          event.preventDefault();
          this.withdraw();
        });
        return;
      }

      if (this.state === 'withdrawn') {
        this.innerHTML = `<p class="room-submit-card__message" role="status">${escapeHtml(i18n.withdrawn)}</p>`;
        return;
      }

      const submitting = this.state === 'submitting';
      this.innerHTML = `
        <div class="room-submit-card room-submit-card--expanded">
          <h3 class="room-submit-card__title">${escapeHtml(i18n.offer_title)}</h3>
          <p class="room-submit-card__body">${escapeHtml(i18n.offer_body)}</p>
          ${this.error ? `<p class="room-submit-card__error form-message form-message--error form-message--small" role="alert">${escapeHtml(this.error)}</p>` : ''}
          <label class="room-submit-card__consent">
            <input type="checkbox" data-consent-input${submitting ? ' disabled' : ''}>
            <span>${escapeHtml(i18n.consent_label)}</span>
          </label>
          <div class="form-field room-submit-card__field">
            <label class="form-label" for="${this.fieldId}">${escapeHtml(i18n.room_name_label)}</label>
            <input type="text" class="form-control" id="${this.fieldId}" data-room-name-input maxlength="40" placeholder="${escapeHtml(i18n.room_name_placeholder)}"${submitting ? ' disabled' : ''}>
          </div>
          <div class="room-submit-card__actions">
            <button type="button" class="btn btn--primary room-submit-card__submit" data-submit-btn disabled${submitting ? ' aria-busy="true"' : ''}>${escapeHtml(i18n.submit)}</button>
            <a href="#" class="room-submit-card__link" data-decline-link>${escapeHtml(i18n.decline)}</a>
          </div>
        </div>`;

      const consentInput = this.querySelector('[data-consent-input]');
      const submitBtn = this.querySelector('[data-submit-btn]');
      consentInput.addEventListener('change', () => { submitBtn.disabled = submitting || !consentInput.checked; });
      submitBtn.disabled = submitting || !consentInput.checked;
      submitBtn.addEventListener('click', () => this.submit());
      this.querySelector('[data-decline-link]').addEventListener('click', (event) => {
        event.preventDefault();
        this.remove();
      });
    }

    async submit() {
      if (this.state === 'submitting') return;
      const i18n = resolveI18n();
      const roomName = (this.querySelector('[data-room-name-input]')?.value || '').trim().slice(0, 40);
      this.state = 'submitting';
      this.render();

      const base = this.workerUrl();
      if (!base) {
        this.fail('generic', i18n.error_generic);
        return;
      }

      let response;
      try {
        response = await fetch(`${base}/submit`, {
          method: 'POST',
          credentials: 'omit',
          signal: AbortSignal.timeout(10000),
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            job_id: this.jobId,
            ...(roomName ? { room_label: roomName } : {}),
            consent_owner: true,
            consent_no_people: true,
          }),
        });
      } catch {
        this.fail('generic', i18n.error_generic);
        return;
      }

      if (response.status === 201) {
        const body = await response.json().catch(() => null);
        storeSubmission(this.jobId, body?.takedown_token);
        track('room_preview_submitted', this.jobId, this.productContext);
        this.state = 'submitted';
        this.error = '';
        this.render();
        return;
      }
      if (response.status === 409) { this.fail('duplicate', i18n.error_duplicate); return; }
      if (response.status === 429) { this.fail('rate_limited', i18n.error_rate_limited); return; }
      this.fail('generic', i18n.error_generic);
    }

    fail(reason, message) {
      this.error = message;
      this.state = 'expanded';
      this.render();
      track('room_preview_submission_failed', this.jobId, this.productContext, { reason });
    }

    async withdraw() {
      const existing = findSubmission(this.jobId);
      const base = this.workerUrl();
      if (existing && base) {
        try {
          await fetch(`${base}/withdraw`, {
            method: 'POST',
            credentials: 'omit',
            signal: AbortSignal.timeout(10000),
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ job_id: existing.jobId, takedown_token: existing.takedown_token }),
          });
        } catch {
          /* best effort: still clear the local record so the shopper isn't stuck */
        }
      }
      removeSubmission(this.jobId);
      track('room_submission_withdrawn', this.jobId, this.productContext);
      this.state = 'withdrawn';
      this.render();
    }
  }

  class RealRoomsStrip extends HTMLElement {
    static get observedAttributes() {
      return ['data-handle', 'data-worker-url'];
    }

    connectedCallback() {
      this.rooms = [];
      this.slidesAdded = false;
      this.refresh();
    }

    attributeChangedCallback() {
      if (!this.isConnected) return;
      this.refresh();
    }

    refresh() {
      const handle = this.dataset.handle || '';
      const base = (this.dataset.workerUrl || '').replace(/\/$/, '');
      if (!handle || !base || handle === this.loadedHandle) return;
      this.loadedHandle = handle;
      this.load(handle, base);
    }

    async load(handle, base) {
      this.setAttribute('hidden', '');
      let response;
      try {
        response = await fetch(`${base}/rooms/${encodeURIComponent(handle)}`, {
          credentials: 'omit',
          signal: AbortSignal.timeout(10000),
          headers: { Accept: 'application/json' },
        });
      } catch {
        return;
      }
      if (!response.ok) return;
      let body;
      try { body = await response.json(); } catch { return; }
      const rooms = Array.isArray(body?.rooms) ? body.rooms : [];
      const valid = rooms.filter((room) => room && isHttpsImageUrl(room.image_url));
      if (!valid.length) return;
      this.rooms = valid.slice(0, 5);
      this.slidesAdded = false;
      this.renderRooms(rooms.length);
      this.removeAttribute('hidden');
    }

    renderRooms(totalCount) {
      const i18n = resolveI18n();
      const heading = this.dataset.heading || format(i18n.strip_heading, { count: totalCount });
      this.innerHTML = `
        <h3 class="real-rooms-strip__heading">${escapeHtml(heading)}</h3>
        <div class="real-rooms-strip__row">
          ${this.rooms.map((room, index) => `
            <button type="button" class="real-rooms-strip__tile" data-room-index="${index}">
              <img class="real-rooms-strip__image" src="${escapeHtml(room.image_url)}" alt="" loading="lazy" width="52" height="52">
            </button>`).join('')}
          <button type="button" class="real-rooms-strip__tile real-rooms-strip__tile--yours" data-yours>
            <span>${escapeHtml(i18n.yours)}</span>
          </button>
        </div>`;

      this.querySelectorAll('[data-room-index]').forEach((button) => {
        button.addEventListener('click', () => this.openRoom(this.rooms[Number(button.dataset.roomIndex)]));
      });
      this.querySelector('[data-yours]')?.addEventListener('click', () => window.LampsRoomGallery?.open?.());
    }

    ensureSlidesAdded() {
      if (this.slidesAdded) return;
      const i18n = resolveI18n();
      const slides = this.rooms.map((room) => ({
        id: room.id,
        imageUrl: room.image_url,
        caption: room.room_label
          ? format(i18n.caption_labelled, { room: String(room.room_label).toLowerCase() })
          : i18n.caption_plain,
      }));
      window.LampsRoomGallery?.addSlides?.(slides, { group: 'real-rooms' });
      this.slidesAdded = true;
    }

    openRoom(room) {
      if (!room) return;
      this.ensureSlidesAdded();
      const gallery = window.LampsRoomGallery;
      if (typeof gallery?.open === 'function') {
        if (gallery.open.length > 0) gallery.open(room.id);
        else gallery.open();
      }
      window.LampsRoomStudio?.track('real_room_viewed', {}, { share_id: room.id });
    }
  }

  if (!customElements.get('room-submit-card')) customElements.define('room-submit-card', RoomSubmitCard);
  if (!customElements.get('real-rooms-strip')) customElements.define('real-rooms-strip', RealRoomsStrip);

  document.addEventListener('room:generated', (event) => {
    const slot = document.querySelector('.swiper-slide:not(.swiper-slide-duplicate) [data-room-community-slot], [data-room-community-slot]');
    const detail = event.detail || {};
    if (!slot || !detail.jobId) return;
    slot.innerHTML = '';
    const card = document.createElement('room-submit-card');
    card.jobId = detail.jobId;
    card.productContext = detail.product || null;
    slot.appendChild(card);
  });
})();
